<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'

import {
  summaryPanelClass,
  summaryPanelHeaderClass,
  summaryPanelTabsClass,
  summaryPanelTitleClass,
} from './summaryPanelClasses'

import {
  DataVisible,
  ReportSectionTitle,
  ReportToolbarSelect,
  REPORT_TOOLBAR_ICONS,
  SUMMARY_ACQUISITION_TABLE_CONFIG,
  useCustomizeStore,
  useReportData,
  useReportSchedules,
  type ReportFilter,
  type ScheduleTab,
  type ReportSpec,
} from '@/features/reports'
import { useI18n } from '@/shared/i18n'
import { Alert, Button, type SelectOption } from '@/shared/ui'

const props = withDefaults(defineProps<{ defaultPostData?: string }>(), { defaultPostData: '' })
const { t } = useI18n()
const cache = useCustomizeStore()
const schedules = useReportSchedules()
const types = ref<string[]>(['All'])
const selectedType = ref('All')
const probeSpec = computed<ReportSpec>(() => ({
  report_id: 50,
  dimensions: ['platform'],
  pagination_enabled: false,
  partition_name: 'overall_ga4_data',
  ...(props.defaultPostData ? { defaultPostData: props.defaultPostData } : {}),
}))
const probe = useReportData(probeSpec, { immediate: false })
const options = computed<SelectOption<string>[]>(() =>
  types.value.map((value) => ({
    value,
    label: value === 'All' ? t('summary.acquisition.allPlatforms') : value,
  })),
)
const filters = computed<ReportFilter[]>(() =>
  selectedType.value === 'All'
    ? []
    : [{ field: 'platform', match_mode: 'equals', value: selectedType.value }],
)
const scheduleTab = computed<ScheduleTab | null>(() => {
  const tabs = schedules.getScheduleByReportId(50)?.additional_info?.tabs ?? {}
  for (const value of Object.values(tabs)) {
    if (typeof value === 'object' && value !== null && value.partition_name === 'overall_ga4_data')
      return value
  }
  return null
})
const acquisitionDimensions = computed(() => {
  const productionOrder = ['platform', 'session_medium', 'session_source']
  const scheduled = new Set([
    ...(scheduleTab.value?.columns ?? []),
    ...(scheduleTab.value?.dimensions ?? []),
  ])
  return [
    'platform',
    ...productionOrder.slice(1).filter((field) => scheduled.size === 0 || scheduled.has(field)),
  ]
})
const acquisitionMetrics = computed(() =>
  scheduleTab.value?.metrics?.length ? [...scheduleTab.value.metrics] : ['mp_sessions'],
)
const acquisitionFields = computed(() => [
  ...new Set([...acquisitionDimensions.value, ...acquisitionMetrics.value]),
])

function hasTableDropdown(
  config: { dropdowns?: readonly { id: string }[] } | undefined,
  id: string,
): boolean {
  return (config?.dropdowns ?? SUMMARY_ACQUISITION_TABLE_CONFIG.dropdowns).some(
    (dropdown) => dropdown.id === id,
  )
}

watch(
  () => probe.state.value,
  (state) => {
    if (state !== 'ready') return
    types.value = [
      'All',
      ...new Set(
        probe.rows.value.map((row) => String(row.platform ?? '')).filter((v) => v && v !== '-'),
      ),
    ]
    const saved = cache.getCached<{ selectedType?: string }>('product-dashboard', 'selections')
    selectedType.value =
      saved?.selectedType && types.value.includes(saved.selectedType) ? saved.selectedType : 'All'
  },
)
watch(selectedType, (value) =>
  cache.setCached('product-dashboard', 'selections', { selectedType: value }),
)
onMounted(() => void probe.reload())
</script>

<template>
  <section :class="summaryPanelClass">
    <header :class="[summaryPanelHeaderClass, 'mb-sm']">
      <ReportSectionTitle
        :title="t('summary.acquisition.tableTitle')"
        :tabs="[t('summary.acquisition.overallTab')]"
        :title-class="summaryPanelTitleClass"
        :tabs-class="summaryPanelTabsClass"
      />
    </header>
    <Alert v-if="probe.state.value === 'error'" tone="warning">
      {{ t('summary.acquisition.probeError') }}
      <Button size="sm" variant="outline" @click="probe.reload">{{
        t('summary.actions.retry')
      }}</Button>
    </Alert>
    <DataVisible
      id="50-traffic-acquisition"
      v-bind="{ ariaLabel: t('summary.acquisition.tableLabel') }"
      :report-id="50"
      :default-post-data="defaultPostData"
      :dimensions="acquisitionDimensions"
      :metrics="acquisitionMetrics"
      :available-fields="acquisitionFields"
      :chart-dimensions="['session_source']"
      :allowed-views="['table', 'chart']"
      :allowed-chart-types="['column', 'pie']"
      :table-config="SUMMARY_ACQUISITION_TABLE_CONFIG"
      :filters="filters"
      add-compare
      partition-name="overall_ga4_data"
      sort-field="mp_sessions"
      :empty-label="t('summary.states.empty')"
      :error-label="t('summary.states.error')"
      :retry-label="t('summary.actions.retry')"
    >
      <template #controls="{ tableConfig }">
        <div
          v-if="hasTableDropdown(tableConfig, 'platform-filter')"
          class="summary-acquisition__controls flex"
        >
          <ReportToolbarSelect
            v-model="selectedType"
            :label="t('summary.acquisition.platform')"
            :options="options"
            :icon="REPORT_TOOLBAR_ICONS.platform"
          />
        </div>
      </template>
    </DataVisible>
  </section>
</template>
